import React from 'react';
import { Search, X, Pencil, Trash2, Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';
import { INPUT, LABEL } from './billing/ui';

// Shared look for CRM list pages. Vendors is the reference; these are its
// building blocks so every list page gets the same gutters, cards, tables,
// forms, modals and row actions instead of re-spelling them per page.
export { INPUT, LABEL };

// Outer wrapper of a page (DashboardLayout already pads the content area).
export const PAGE = 'p-4 md:p-6 space-y-5';
export const CARD = 'bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]';
export const TABLE = 'w-full text-[13px]';
export const THEAD = 'text-[#706E6B] text-[11px] uppercase tracking-wide';
export const TH = 'text-left font-semibold px-4 py-2.5';
export const TD = 'px-4 py-2.5';
export const TR = 'border-b border-[#F3F2F2] hover:bg-[#F7F9FB]';
export const SECTION_TITLE = 'text-[12px] font-bold text-[#706E6B] uppercase tracking-wide mb-2';
export const NOTE = 'text-[11px] text-[#706E6B]';

// Primary / secondary action buttons used in headers and modal footers.
export const BTN_PRIMARY = 'h-9 px-4 rounded bg-[#0176D3] text-white text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-60 inline-flex items-center justify-center gap-1.5 whitespace-nowrap';
export const BTN_SECONDARY = 'h-9 px-4 rounded border border-[#C9C9C9] bg-white text-[13px] font-semibold text-[#444444] hover:bg-[#F3F2F2] inline-flex items-center justify-center gap-1.5 whitespace-nowrap';

// Label + control. `children` is the control; pass `htmlFor` when it has an id.
export function Field({ label, children, className, htmlFor }) {
  return (
    <div className={className}>
      <label className={LABEL} htmlFor={htmlFor}>{label}</label>
      {children}
    </div>
  );
}

export function Checkbox({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-[13px] font-semibold text-[#2E2E2E] normal-case tracking-normal">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

// Edit / delete icon buttons at the end of a table row.
export function RowActions({ name, onEdit, onDelete, children }) {
  return (
    <td className={cn(TD, 'text-right whitespace-nowrap')}>
      {children}
      {onEdit && (
        <button type="button" onClick={onEdit} aria-label={`Edit ${name}`} title={`Edit ${name}`} className="p-1.5 rounded hover:bg-[#F3F2F2] text-[#706E6B]"><Pencil size={14} /></button>
      )}
      {onDelete && (
        <button type="button" onClick={onDelete} aria-label={`Delete ${name}`} title={`Delete ${name}`} className="p-1.5 rounded hover:bg-[#F3F2F2] text-[#706E6B] hover:text-[#BA0517]"><Trash2 size={14} /></button>
      )}
    </td>
  );
}

// The "Loading…" / empty-state row of a table — the page stays up while
// data loads instead of being replaced by a full-page spinner.
export function TableMessage({ colSpan, loading, empty = 'Nothing here yet.', show = true }) {
  if (!show) return null;
  return (
    <tr><td colSpan={colSpan} className="px-4 py-12 text-center text-[#706E6B]">{loading ? 'Loading…' : empty}</td></tr>
  );
}

// Small grey/green pill, e.g. Active / Inactive.
export function StatusPill({ active, activeLabel = 'Active', inactiveLabel = 'Inactive' }) {
  return (
    <span className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded', active ? 'bg-[#E3F3E8] text-[#04844B]' : 'bg-[#F3F2F2] text-[#706E6B]')}>
      {active ? activeLabel : inactiveLabel}
    </span>
  );
}

// Status tabs on the left and a search box on the right, above a table.
export function ListToolbar({ tabs, value, onChange, search, onSearch, placeholder = 'Search…' }) {
  return (
    <div className="flex flex-wrap items-center gap-2 p-3 border-b border-[#D8DDE6]">
      {tabs && tabs.map(([key, label]) => (
        <button type="button" key={label} onClick={() => onChange(key)} className={cn('h-8 px-3 rounded text-[12px] font-semibold border', value === key ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white text-[#444444] border-[#D8DDE6]')}>{label}</button>
      ))}
      {onSearch && (
        <div className="relative ml-auto w-full sm:w-72">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#706E6B]" />
          <input aria-label="Search" className={cn(INPUT, 'pl-8')} placeholder={placeholder} value={search} onChange={(e) => onSearch(e.target.value)} />
        </div>
      )}
    </div>
  );
}

// Create/edit form in a modal over the list, matching the Vendors form.
export function FormModal({ title, onClose, onSubmit, saving, submitLabel = 'Save', children, size = 'max-w-3xl' }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-end sm:items-start justify-center overflow-y-auto sm:p-4" onMouseDown={onClose}>
      <form
        className={cn('bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full sm:my-8', size)}
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
        role="dialog"
        aria-label={title}
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <h2 className="text-[16px] font-bold text-[#2E2E2E]">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-[#F3F2F2]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5">{children}</div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#D8DDE6] bg-[#FAFAF9] rounded-b-lg">
          <button type="button" onClick={onClose} className={BTN_SECONDARY}>Cancel</button>
          <button type="submit" disabled={saving} className={BTN_PRIMARY}>{saving ? 'Saving…' : submitLabel}</button>
        </div>
      </form>
    </div>
  );
}

// One loading state for every page: a card with a spinner inside the standard
// page wrapper, so the gutters don't jump when the data arrives. `inline` is for
// a loading area inside a tab or panel that already has its own wrapper.
export function PageLoader({ inline = false, label = 'Loading…' }) {
  const body = (
    <div className={cn(CARD, 'px-4 py-12 flex items-center justify-center gap-2 text-[#706E6B] text-[13px]')} role="status" aria-live="polite">
      <Loader2 size={16} className="animate-spin text-[#0176D3]" />
      {label}
    </div>
  );
  return inline ? body : <div className={PAGE}>{body}</div>;
}
